/**
 * Line element renderer
 * Extracted from Canvas.jsx to reduce file size
 */
import React from "react";
import UnifiedLineArrowElement from "../../unified/UnifiedLineArrowElement";
import { isElementInViewport } from "../../../utils/viewportCullingUtils";

// DRAG THROTTLING: Match imageRenderer behavior to prevent fluctuation when images are present
// Reduced from 80ms to 16ms for better cross-canvas drag performance
const DRAG_STATE_THROTTLE_MS = 16;
let _lastLineRendererDragStateTime = 0;

/**
 * Creates a line element renderer factory function
 */
export const createLineRenderer = ({
  lines,
  offCanvasLines,
  setLines,
  setOffCanvasLines,
  selectedObject,
  strokeColor,
  canvasResizeTrigger,
  effectiveZoomLevel,
  disableInteraction,
  debouncedStateUpdate,
  isTransformingRef,
  updateLineProperties,
  setSelectedObject,
  setSelectedObjects,
  selectedObjects = [],
  setShowTextBox,
  setSelectedText,
  setSelectedImage,
  setSelectedLine,
  setSelectedArrow,
  setSelectedDoubleSidedArrow,
  onClick,
  handleElementDragEnd,
  handleLineDragEnd, // CRITICAL: Handler for updating lines array after drag
  saveToHistory,
  handleDragPreview,
  clearDragPreview,
  handleElementCopy,
  canvasWidth,
  canvasHeight,
  lockedAction,
  isCloneMode = false, // Clone mode flag (additional check for clone mode)
  isElementBeingEdited = false,
  setPendingChanges = () => {},
  isMultiSelectMode = false, // Multi-select mode flag
  handleGroupDragStart = null, // Handler for group drag start
  handleGroupDragMove = null, // Handler for group drag move
  cancelActiveGroupDrag = null,
  // Element arrays for cross-canvas selection check
  rectangles = [],
  offCanvasRectangles = [],
  circles = [],
  offCanvasCircles = [],
  images = [],
  offCanvasImages = [],
  texts = [],
  offCanvasTexts = [],
  arrows = [],
  offCanvasArrows = [],
  doubleSidedArrows = [],
  offCanvasDoubleSidedArrows = [],
  stageRef = null, // Main canvas stage ref (for coordinate conversion during drag)
  offCanvasStageRef = null, // Off-canvas stage ref (for coordinate conversion during drag)
  dragPreviewStateRef = null, // When cross-canvas preview is active for this line, only Canvas handleDragPreview updates editor
  blockClicksOnOffCanvasElements = false,
  blockClicksOnMainStageElements = false,
}) => {
  return ({
    line,
    isOffCanvas = false,
    sourceStageRef,
    saveToHistory: saveToHistoryParam,
    isPreview = false,
    opacity = undefined, // Opacity override for dimming non-selected elements
  }) => {
    // Helper function to check if an element is in main canvas
    const isElementInMainCanvas = (elementId, elementType) => {
      if (!elementId || !elementType) return false;
      switch (elementType) {
        case "Rect":
          return rectangles.some((r) => r.id === elementId);
        case "Circle":
          return circles.some((c) => c.id === elementId);
        case "Image":
          return images.some((i) => i.id === elementId);
        case "Text":
          return texts.some((t) => t.id === elementId);
        case "Arrow":
          // Check arrows array first
          if (arrows.some((a) => a.id === elementId)) return true;
          // If not found, check lines array for unified arrow elements (with arrow heads)
          return lines.some((l) => {
            if (l.id !== elementId) return false;
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            return hasStartArrow || hasEndArrow;
          });
        case "Line":
          // Lines without arrow heads are in the lines array
          return lines.some((l) => {
            if (l.id !== elementId) return false;
            // Only return lines without arrow heads
            return (
              !l.hasStartArrow &&
              !l.hasEndArrow &&
              !l.pointerAtBeginning &&
              !l.pointerAtEnd
            );
          });
        case "DoubleSidedArrow":
          // Check doubleSidedArrows array first
          if (doubleSidedArrows.some((d) => d.id === elementId)) return true;
          // If not found, check lines array for unified double-sided arrow elements
          return lines.some((l) => {
            if (l.id !== elementId) return false;
            const hasStartArrow =
              l.hasStartArrow === true || l.pointerAtBeginning === true;
            const hasEndArrow =
              l.hasEndArrow === true || l.pointerAtEnd === true;
            return hasStartArrow && hasEndArrow;
          });
        default:
          return false;
      }
    };
    // CRITICAL: If this line is selected, merge properties from selectedObject to ensure we have the latest values
    // This is important because selectedObject is updated immediately, but array updates might be batched or delayed
    // Check if selected first (before merging) to determine if we should merge
    const isInteractive = !isPreview;
    const isSelected =
      isInteractive &&
      line.id === selectedObject?.id &&
      (selectedObject?.type === "Line" ||
        selectedObject?.type === "Arrow" ||
        selectedObject?.type === "DoubleSidedArrow");

    // When preview is on the OTHER stage (ghost drawn there), keep SOURCE-stage line at source position
    // so it doesn't jump; only the ghost (handleDragPreview) moves. Circles don't need this because
    // circle is a single point; line has Group+points and was getting stuck when we applied converted coords.
    const previewRef = dragPreviewStateRef?.current;
    const sourceStageTarget = isOffCanvas ? "offCanvas" : "main";
    const isPreviewOnOtherStage =
      previewRef?.id === line.id && previewRef?.target !== sourceStageTarget;

    // CRITICAL FIX: During group drag, do NOT merge with selectedObjects for rendering
    // selectedObjects has converted coordinates relative to target canvas (for editor/inspector)
    // but Konva nodes are still on source canvas with source coordinates
    // Merging would cause wrong positions during rendering
    const justFinishedGroupDrag = window.clearLastDraggedPositions === true;
    const isInMultiSelection =
      selectedObjects &&
      selectedObjects.length > 1 &&
      selectedObjects.some((obj) => obj.id === line.id);

    // Check if we're currently in a group drag
    const isCurrentlyGroupDragging =
      isInMultiSelection && !justFinishedGroupDrag;

    // CRITICAL: During group drag, use array-backed element only (don't merge with selectedObjects)
    // After group drag finishes, also use array-backed element
    // Only merge with selectedObject for individual selection (not multi-selection)
    const effectiveLine =
      isCurrentlyGroupDragging || justFinishedGroupDrag
        ? line // Use array-backed element during and after group drag
        : isSelected && selectedObject && selectedObject.id === line.id
          ? isPreviewOnOtherStage
            ? {
                ...line,
                ...selectedObject,
                x: line.x,
                y: line.y,
                points: line.points ?? selectedObject.points,
                opacity: line.opacity,
              }
            : { ...line, ...selectedObject, opacity: line.opacity }
          : line;

    // Use provided opacity or element's own opacity, defaulting to 1
    const elementOpacity =
      opacity !== undefined
        ? opacity
        : effectiveLine.opacity !== undefined
          ? effectiveLine.opacity
          : 1;
    const setLineState = isOffCanvas ? setOffCanvasLines : setLines;

    // Check if element is in selectedObjects array (for multi-selection)
    const isInSelectedObjects =
      selectedObjects &&
      selectedObjects.some(
        (obj) =>
          obj.id === effectiveLine.id &&
          (obj.type === "Line" ||
            obj.type === "Arrow" ||
            obj.type === "DoubleSidedArrow"),
      );

    // When duplicate-to-selected-location is on, no drag (single or group). Otherwise draggable if selected or in multi-selection.
    const isDraggableElement =
      !isCloneMode &&
      !lockedAction &&
      (isSelected || (isInSelectedObjects && selectedObjects.length > 1));

    const effectiveSaveToHistory = saveToHistoryParam || saveToHistory;

    // Convert line format to unified element format
    // CRITICAL: Normalize type first to handle both lowercase and uppercase
    const normalizedType = effectiveLine.type
      ? effectiveLine.type.toLowerCase() === "arrow"
        ? "Arrow"
        : effectiveLine.type.toLowerCase() === "double_sided_arrow" ||
            effectiveLine.type === "DoubleSidedArrow"
          ? "DoubleSidedArrow"
          : effectiveLine.type
      : null;

    // CRITICAL: Respect the actual arrow flags from the line data
    // Don't override based on type - the type should be determined FROM the flags, not the other way around
    // This allows independent control of start and end arrows via checkboxes
    let hasStartArrow =
      effectiveLine.hasStartArrow !== undefined
        ? effectiveLine.hasStartArrow === true
        : effectiveLine.pointerAtBeginning !== undefined
          ? effectiveLine.pointerAtBeginning === true
          : false;

    let hasEndArrow =
      effectiveLine.hasEndArrow !== undefined
        ? effectiveLine.hasEndArrow === true
        : effectiveLine.pointerAtEnd !== undefined
          ? effectiveLine.pointerAtEnd === true
          : false;

    // Only apply type-based defaults if arrow flags are not explicitly set (for backward compatibility)
    if (
      effectiveLine.hasStartArrow === undefined &&
      effectiveLine.hasEndArrow === undefined &&
      effectiveLine.pointerAtBeginning === undefined &&
      effectiveLine.pointerAtEnd === undefined
    ) {
      // CRITICAL: Normalize element type to handle both lowercase and uppercase
      const normalizedElementType = normalizedType;

      // Only apply defaults if flags are completely unset
      if (normalizedElementType === "Arrow") {
        // Default arrows to have pointer at end only
        hasEndArrow = true;
        hasStartArrow = false;
      } else if (normalizedElementType === "DoubleSidedArrow") {
        // Default double-sided arrows to have pointers at both ends
        hasStartArrow = true;
        hasEndArrow = true;
      }
    }

    const element = {
      ...effectiveLine,
      opacity: elementOpacity,
      hasStartArrow: hasStartArrow,
      hasEndArrow: hasEndArrow,
      // CRITICAL: Normalize type to uppercase for consistency
      type: normalizedType || effectiveLine.type,
    };

    // Debug log to track flag changes in renderer

    // Helper function to validate and fix points for selectedElement
    const validateSelectedElementPoints = (selectedElement, sourceElement) => {
      if (!selectedElement.points || selectedElement.points.length < 4) {
        return selectedElement;
      }
      const pointsLength = Math.sqrt(
        Math.pow(selectedElement.points[2] - selectedElement.points[0], 2) +
          Math.pow(selectedElement.points[3] - selectedElement.points[1], 2),
      );
      if (pointsLength >= 0.1) {
        return selectedElement; // Points are valid
      }
      // Points are invalid, try to use source element's points
      if (
        sourceElement &&
        sourceElement.points &&
        sourceElement.points.length >= 4
      ) {
        const elementLength = Math.sqrt(
          Math.pow(sourceElement.points[2] - sourceElement.points[0], 2) +
            Math.pow(sourceElement.points[3] - sourceElement.points[1], 2),
        );
        if (elementLength >= 0.1) {
          // Use source element's points which are valid
          return { ...selectedElement, points: [...sourceElement.points] };
        }
      }
      // Both are invalid, use default
      const defaultLength =
        sourceElement?.width && sourceElement.width > 0
          ? sourceElement.width
          : 100;
      return { ...selectedElement, points: [0, 0, defaultLength, 0] };
    };

    // CRITICAL: If selected, merge selectedObject with element, but prioritize element.points
    // if selectedObject.points would result in zero length after conversion
    // CRITICAL FIX: During group drag, do NOT merge with selectedObjects for rendering
    // selectedObjects has converted coordinates, but we need source coordinates for rendering

    let elementToUse =
      isCurrentlyGroupDragging || justFinishedGroupDrag
        ? element // Use array-backed element during and after group drag
        : isSelected && selectedObject && selectedObject.id === element.id
          ? isPreviewOnOtherStage
            ? {
                ...element,
                ...selectedObject,
                x: element.x,
                y: element.y,
                points: element.points ?? selectedObject.points,
                opacity: element.opacity,
              }
            : { ...element, ...selectedObject, opacity: element.opacity }
          : element;

    // CRITICAL: Check if selectedObject.points are valid before using them
    // If they would result in zero length, use element.points instead (source of truth from lines array)
    // If both are invalid, compute a default valid line
    // CRITICAL FIX: Skip this validation for group drag - use element as-is
    if (
      !isCurrentlyGroupDragging &&
      !justFinishedGroupDrag &&
      isSelected &&
      selectedObject &&
      selectedObject.id === element.id &&
      selectedObject.points &&
      selectedObject.points.length >= 4 &&
      element.points &&
      element.points.length >= 4
    ) {
      // Check if selectedObject.points would result in zero length
      const selectedPoints = selectedObject.points;
      const selectedLength = Math.sqrt(
        Math.pow(selectedPoints[2] - selectedPoints[0], 2) +
          Math.pow(selectedPoints[3] - selectedPoints[1], 2),
      );

      // Also check if converting absolute points would result in zero length
      let convertedLength = selectedLength;
      if (elementToUse.x !== undefined && elementToUse.y !== undefined) {
        const firstPointX = selectedPoints[0];
        const firstPointY = selectedPoints[1];
        const tolerance = 0.1;
        if (
          Math.abs(firstPointX - elementToUse.x) < tolerance &&
          Math.abs(firstPointY - elementToUse.y) < tolerance
        ) {
          // Points are absolute, check length after conversion
          const convertedPoints = selectedPoints.map((coord, i) => {
            return i % 2 === 0
              ? coord - elementToUse.x
              : coord - elementToUse.y;
          });
          convertedLength = Math.sqrt(
            Math.pow(convertedPoints[2] - convertedPoints[0], 2) +
              Math.pow(convertedPoints[3] - convertedPoints[1], 2),
          );
        }
      }

      // Check if element.points are also invalid
      const elementLength = Math.sqrt(
        Math.pow(element.points[2] - element.points[0], 2) +
          Math.pow(element.points[3] - element.points[1], 2),
      );

      // If selectedObject.points would result in zero length, use element.points if valid, otherwise use default
      if (convertedLength < 0.1) {
        if (elementLength >= 0.1) {
          // element.points are valid, use them
          elementToUse = { ...elementToUse, points: [...element.points] };
        } else {
          // Both are invalid, use a default valid line based on width or default length
          const defaultLength =
            elementToUse.width && elementToUse.width > 0
              ? elementToUse.width
              : 100;
          const defaultPoints = [0, 0, defaultLength, 0];
          elementToUse = { ...elementToUse, points: defaultPoints };
        }
      }
    }

    // CRITICAL: Ensure points are relative coordinates (relative to Group position)
    // If points appear to be absolute (start at element.x, element.y), convert them to relative
    if (
      elementToUse.points &&
      elementToUse.points.length >= 4 &&
      elementToUse.x !== undefined &&
      elementToUse.y !== undefined
    ) {
      const firstPointX = elementToUse.points[0];
      const firstPointY = elementToUse.points[1];
      // Check if points are absolute (start at Group position)
      const tolerance = 0.1;
      if (
        Math.abs(firstPointX - elementToUse.x) < tolerance &&
        Math.abs(firstPointY - elementToUse.y) < tolerance
      ) {
        // Points are absolute, convert to relative
        const relativePoints = elementToUse.points.map((coord, i) => {
          return i % 2 === 0
            ? coord - elementToUse.x // X coordinate: subtract group X
            : coord - elementToUse.y; // Y coordinate: subtract group Y
        });
        elementToUse = { ...elementToUse, points: relativePoints };
      }
    }

    // CRITICAL: Recompute arrow flags from elementToUse to ensure key changes when flags change
    const finalHasStartArrow = elementToUse.hasStartArrow === true;
    const finalHasEndArrow = elementToUse.hasEndArrow === true;

    // Viewport culling: skip rendering lines/arrows far outside visible area
    const shouldAlwaysRender = isPreview || isSelected || isInSelectedObjects;
    if (!shouldAlwaysRender) {
      const stage = isOffCanvas
        ? offCanvasStageRef?.current
        : stageRef?.current;
      if (stage && !isElementInViewport(elementToUse, "Line", stage)) {
        return null;
      }
    }

    // Extract onSelect handler to avoid Babel parsing issues with complex ternary in JSX
    let handleSelect = undefined;
    if (setSelectedObject) {
      handleSelect = (e, selectedElement) => {
        // Handle selection similar to LineElement
        // Disable selection if a tool is locked or clone mode is active
        if (lockedAction || isCloneMode) {
          return;
        }

        // When multi-select is on and selection is from the other stage, prevent clicking on this stage's elements
        if (isOffCanvas && blockClicksOnOffCanvasElements) {
          e?.evt?.preventDefault?.();
          e?.evt?.stopPropagation?.();
          return;
        }
        if (!isOffCanvas && blockClicksOnMainStageElements) {
          e?.evt?.preventDefault?.();
          e?.evt?.stopPropagation?.();
          return;
        }

        const isSameElement =
          selectedObject && selectedObject.id === selectedElement.id;

        // CRITICAL: When multi-select mode is enabled, toggle membership (match rectangleRenderer / arrowRenderer).
        // Previously: branch required !isSameElement (primary click did nothing) and "already in set" returned without removing.
        if (isMultiSelectMode) {
          const currentSelectionArray = [];
          const currentSelectionSet = new Set();

          if (selectedObjects && selectedObjects.length > 0) {
            selectedObjects.forEach((obj) => {
              if (!currentSelectionSet.has(obj.id)) {
                currentSelectionArray.push(obj);
                currentSelectionSet.add(obj.id);
              }
            });
          }

          if (selectedObject && !currentSelectionSet.has(selectedObject.id)) {
            currentSelectionArray.push(selectedObject);
            currentSelectionSet.add(selectedObject.id);
          }

          if (currentSelectionSet.has(selectedElement.id)) {
            cancelActiveGroupDrag?.();
            const nextSelection = currentSelectionArray.filter(
              (obj) => obj.id !== selectedElement.id,
            );
            const nextPrimary = nextSelection[0] || null;
            const nextLineLike =
              nextPrimary &&
              (nextPrimary.type === "Line" ||
                nextPrimary.type === "Arrow" ||
                nextPrimary.type === "DoubleSidedArrow")
                ? nextPrimary
                : null;
            if (setSelectedObjects) {
              setSelectedObjects(nextSelection);
            }
            setSelectedObject(nextPrimary);
            setSelectedLine?.(nextLineLike);
            if (!nextLineLike) {
              if (setSelectedArrow) setSelectedArrow(null);
              if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
            }
            let n = e?.target;
            while (n) {
              if (typeof n.stopDrag === "function") {
                try {
                  n.stopDrag();
                } catch (_) {
                  /* ignore */
                }
                break;
              }
              n = n.getParent?.() ?? null;
            }
            if (e) {
              e.evt?.stopPropagation?.();
              e.evt?.preventDefault?.();
              e.evt?.stopImmediatePropagation?.();
            }
            return;
          }

          // Cross-canvas selection check
          if (currentSelectionArray.length > 0) {
            // Determine canvas type of all currently selected elements
            const selectedElementsCanvasTypes = currentSelectionArray.map(
              (obj) => isElementInMainCanvas(obj.id, obj.type),
            );
            const allFromMainCanvas = selectedElementsCanvasTypes.every(
              (isMain) => isMain === true,
            );
            const allFromOffCanvas = selectedElementsCanvasTypes.every(
              (isMain) => isMain === false,
            );

            // Determine element type (could be Line, Arrow, or DoubleSidedArrow for unified elements)
            const elementType = selectedElement.type || "Line";

            // Check if clicked line is from a different canvas
            const clickedLineIsMainCanvas = isElementInMainCanvas(
              selectedElement.id,
              elementType,
            );

            if (
              (allFromMainCanvas && !clickedLineIsMainCanvas) ||
              (allFromOffCanvas && clickedLineIsMainCanvas)
            ) {
              if (isMultiSelectMode) {
                // In multi-select mode, don't allow selecting elements from different stages
                if (e) {
                  e.evt?.stopPropagation?.();
                  e.evt?.preventDefault?.();
                }
                return;
              } else {
                // Normal mode cross-canvas behavior:
                // first click clears current selection only.

                // Clear all current selections
                setSelectedObject(null);
                setSelectedObjects([]);
                setShowTextBox(false);
                setSelectedText(null);
                if (setSelectedArrow) {
                  setSelectedArrow(null);
                }
                if (setSelectedDoubleSidedArrow) {
                  setSelectedDoubleSidedArrow(null);
                }
                if (setSelectedLine) {
                  setSelectedLine(null);
                }
                if (e) {
                  e.evt?.stopPropagation?.();
                  e.evt?.preventDefault?.();
                  e.cancelBubble = true;
                }
                return;
              }
            }

            // Add the new element to the selection (only if we didn't just clear selections or prevent in multi-select)
            const shouldAddToSelection = !(
              (allFromMainCanvas && !clickedLineIsMainCanvas) ||
              (allFromOffCanvas && clickedLineIsMainCanvas)
            );
            const nextSelection = shouldAddToSelection
              ? [...currentSelectionArray, selectedElement]
              : [selectedElement]; // If we cleared selections, start fresh with just this element

            setSelectedImage(null);
            setShowTextBox(false);
            setSelectedText(null);
            setSelectedLine?.(selectedElement);
            // CRITICAL: Validate points before setting selectedObject
            const validatedElement = validateSelectedElementPoints(
              selectedElement,
              element,
            );
            setSelectedObject(validatedElement);
            if (setSelectedObjects) {
              setSelectedObjects(nextSelection);
            }
          } else {
            // No current selection, just add this element
            setSelectedImage(null);
            setShowTextBox(false);
            setSelectedText(null);
            setSelectedLine?.(selectedElement);
            // CRITICAL: Validate points before setting selectedObject
            const validatedElement = validateSelectedElementPoints(
              selectedElement,
              element,
            );
            setSelectedObject(validatedElement);
            if (setSelectedObjects) {
              setSelectedObjects([selectedElement]);
            }
          }
          // CRITICAL: Stop event propagation to prevent click handler from overriding selection
          // The onSelect handler has already set the correct type, so we don't want the click handler to override it
          if (e) {
            e.evt?.stopPropagation?.();
            e.evt?.preventDefault?.();
            e.evt?.stopImmediatePropagation?.();
            // Don't call onClick - onSelect has already handled the selection
          }
          return;
        }

        // If clicking on the same element, just return (no action needed)
        if (isSameElement && e) {
          return;
        }

        // Cross-canvas selection check
        const allSelectedElements = [];
        if (
          setSelectedObjects &&
          selectedObjects &&
          selectedObjects.length > 0
        ) {
          selectedObjects.forEach((obj) => {
            if (obj && obj.id && obj.type) {
              allSelectedElements.push(obj);
            }
          });
        }
        if (
          selectedObject &&
          selectedObject.id &&
          selectedObject.type &&
          selectedObject.id !== selectedElement.id // Don't include if it's the same element
        ) {
          const alreadyIncluded = allSelectedElements.some(
            (obj) => obj.id === selectedObject.id,
          );
          if (!alreadyIncluded) {
            allSelectedElements.push(selectedObject);
          }
        }

        // Cross-canvas selection check (only if there are existing selected elements)
        if (allSelectedElements.length > 0) {
          // Check if all selected elements are from the same canvas type
          const selectedElementsCanvasTypes = allSelectedElements.map((obj) =>
            isElementInMainCanvas(obj.id, obj.type),
          );
          const allFromMainCanvas = selectedElementsCanvasTypes.every(
            (isMain) => isMain === true,
          );
          const allFromOffCanvas = selectedElementsCanvasTypes.every(
            (isMain) => isMain === false,
          );

          // Determine element type (could be Line, Arrow, or DoubleSidedArrow for unified elements)
          const elementType = selectedElement.type || "Line";

          // Check if clicked line is from a different canvas
          const clickedLineIsMainCanvas = isElementInMainCanvas(
            selectedElement.id,
            elementType,
          );

          if (
            (allFromMainCanvas && !clickedLineIsMainCanvas) ||
            (allFromOffCanvas && clickedLineIsMainCanvas)
          ) {
            if (isMultiSelectMode) {
              // In multi-select mode, don't allow selecting elements from different stages
              e?.evt?.stopPropagation?.();
              e?.evt?.preventDefault?.();
              return;
            } else {
              // Normal mode cross-canvas behavior:
              // first click clears current selection only.

              // Clear all current selections
              setSelectedObject(null);
              setSelectedObjects([]);
              setShowTextBox(false);
              setSelectedText(null);
              if (setSelectedArrow) {
                setSelectedArrow(null);
              }
              if (setSelectedDoubleSidedArrow) {
                setSelectedDoubleSidedArrow(null);
              }
              if (setSelectedLine) {
                setSelectedLine(null);
              }
              e?.evt?.stopPropagation?.();
              e?.evt?.preventDefault?.();
              e.cancelBubble = true;
              return;
            }
          }
        }

        // CRITICAL: Always proceed with selection (even if there were no previous selections)
        // This ensures UnifiedLineArrowElement gets selected on first click
        setSelectedImage(null);
        setShowTextBox(false);
        setSelectedText(null);
        setSelectedLine?.(selectedElement);
        // CRITICAL: Validate points before setting selectedObject
        const validatedElement = validateSelectedElementPoints(
          selectedElement,
          element,
        );
        setSelectedObject(validatedElement);
        if (setSelectedObjects) {
          setSelectedObjects([selectedElement]);
        }

        // CRITICAL: Stop event propagation to prevent click handler from overriding selection
        // The onSelect handler has already set the correct type, so we don't want the click handler to override it
        if (e) {
          e.evt?.stopPropagation?.();
          e.evt?.preventDefault?.();
          e.evt?.stopImmediatePropagation?.();
          // Don't call onClick - onSelect has already handled the selection
        }
      };
    }

    // For drag previews (ghosts rendered on the opposite canvas), always treat the element
    // as selected and show blue control orbs so they remain visible while dragging.
    const effectiveIsSelected = isPreview ? true : isSelected;
    // Hide blue orbs whenever more than one DISTINCT element id is selected.
    // Dedup by normalized id to match UnifiedLineArrowElement/SelectionEditor behavior.
    const selectedUniqueIdsCount = Array.from(
      new Set(
        (selectedObjects || [])
          .map((obj) =>
            typeof obj?.id === "string" ? obj.id.replace(/-ghost$/, "") : obj?.id,
          )
          .filter(Boolean),
      ),
    ).length;
    const effectiveHideBlueCircles = isPreview
      ? false
      : isCloneMode || selectedUniqueIdsCount > 1;

    // When a cross-canvas preview ghost is active, the source element is typically hidden (opacity 0).
    // In that case, hide the source element's blue orbs and instead show them on the preview ghost.
    const isSourceHiddenForPreview =
      !isPreview &&
      (elementToUse.opacity === 0 || elementToUse.opacity === "0");

    // Suppress the first paint for freshly created draw-lines until pointer-move
    // computes the real geometry/style payload. This avoids a default-style flash.
    if (!isPreview && elementToUse?._deferInitialRender === true) {
      return null;
    }

    return (
      <UnifiedLineArrowElement
        key={
          isPreview
            ? `${effectiveLine.id}-preview-${finalHasStartArrow}-${finalHasEndArrow}`
            : `${effectiveLine.id}-${finalHasStartArrow}-${finalHasEndArrow}`
        }
        element={elementToUse}
        strokeColor={effectiveLine.stroke || strokeColor}
        isDraggable={isInteractive && !disableInteraction && isDraggableElement}
        isSelected={effectiveIsSelected}
        lockedAction={lockedAction}
        isCloneMode={isCloneMode}
        onSelect={handleSelect}
        onClick={isInteractive ? onClick : undefined}
        sourceStageRef={sourceStageRef}
        offCanvasStageRef={offCanvasStageRef}
        mainStageRef={stageRef}
        effectiveZoomLevel={effectiveZoomLevel}
        saveToHistory={effectiveSaveToHistory}
        onDragStart={
          isInteractive && handleGroupDragStart
            ? (element, event) => {
                // Trigger group drag instantly when in multi-select (so no "little move" before group drag)
                const elementType = element.type || "Line";
                const isInSelectedObjects = selectedObjects?.some(
                  (obj) => obj.id === element.id && obj.type === elementType,
                );
                if (
                  selectedObjects &&
                  selectedObjects.length > 1 &&
                  isInSelectedObjects
                ) {
                  handleGroupDragStart(element.id, elementType);
                }
              }
            : undefined
        }
        onDragMove={
          isInteractive
            ? (newAttrs, event) => {
                // If multiple elements are selected AND this element is part of the selection,
                // use group drag handler instead of individual drag (same as circleRenderer)
                let elementType = newAttrs.type;
                if (!elementType) {
                  const hasStart =
                    newAttrs.hasStartArrow === true ||
                    newAttrs.pointerAtBeginning === true;
                  const hasEnd =
                    newAttrs.hasEndArrow === true ||
                    newAttrs.pointerAtEnd === true;
                  elementType =
                    hasStart && hasEnd
                      ? "DoubleSidedArrow"
                      : hasStart || hasEnd
                        ? "Arrow"
                        : "Line";
                }
                const isInSelectedObjects = selectedObjects?.some(
                  (obj) =>
                    obj.id === newAttrs.id &&
                    (obj.type === "Line" ||
                      obj.type === "Arrow" ||
                      obj.type === "DoubleSidedArrow"),
                );
                if (
                  selectedObjects &&
                  selectedObjects.length > 1 &&
                  isInSelectedObjects
                ) {
                  handleGroupDragStart?.(newAttrs.id, elementType);
                  handleGroupDragMove?.(
                    newAttrs.id,
                    elementType,
                    newAttrs.x,
                    newAttrs.y,
                    event,
                  );
                  return;
                }

                // CRITICAL: Match circleRenderer exactly - do NOT transfer state arrays during drag move.
                // Determine target canvas based on mouse position and convert coordinates accordingly.
                let displayAttrs = { ...newAttrs };
                // Always set so editor never has to guess from x (avoids wrong 0 or 1000+); default to source stage
                let previewTargetStage = isOffCanvas ? "offCanvas" : "main";

                if (
                  event?.evt &&
                  sourceStageRef &&
                  offCanvasStageRef?.current
                ) {
                  try {
                    const clientX =
                      event.evt.clientX ?? event.evt.touches?.[0]?.clientX;
                    const clientY =
                      event.evt.clientY ?? event.evt.touches?.[0]?.clientY;

                    const mainStage = stageRef?.current;
                    const offCanvasStage = offCanvasStageRef.current;
                    const mainBBox = mainStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();
                    const offBBox = offCanvasStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();

                    if (
                      mainBBox &&
                      offBBox &&
                      clientX !== undefined &&
                      clientY !== undefined
                    ) {
                      const withinMain =
                        clientX >= mainBBox.left &&
                        clientX <= mainBBox.right &&
                        clientY >= mainBBox.top &&
                        clientY <= mainBBox.bottom;

                      const withinOff =
                        clientX >= offBBox.left &&
                        clientX <= offBBox.right &&
                        clientY >= offBBox.top &&
                        clientY <= offBBox.bottom;

                      const targetCanvas = withinMain
                        ? "main"
                        : withinOff
                          ? "offCanvas"
                          : null;
                      const sourceStage = isOffCanvas ? "offCanvas" : "main";
                      // Always set so editor never has to guess from x; use target when over a stage, else source
                      previewTargetStage = targetCanvas ?? sourceStage;

                      // CRITICAL: If dragging over off-canvas area but element is on main canvas stage,
                      // convert position from main canvas stage coordinates to off-canvas stage coordinates (same as circleRenderer)
                      if (
                        targetCanvas === "offCanvas" &&
                        !isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        const absX = mainBBox.left + newAttrs.x;
                        const absY = mainBBox.top + newAttrs.y;
                        const convertedX = absX - offBBox.left;
                        const convertedY = absY - offBBox.top;
                        displayAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: true,
                        };
                      }
                      // CRITICAL: If dragging over main canvas area but element is on off-canvas stage,
                      // convert position from off-canvas stage coordinates to main canvas stage coordinates (same as circleRenderer)
                      else if (
                        targetCanvas === "main" &&
                        isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        const absX = offBBox.left + newAttrs.x;
                        const absY = offBBox.top + newAttrs.y;
                        const convertedX = absX - mainBBox.left;
                        const convertedY = absY - mainBBox.top;
                        displayAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: false,
                        };
                      } else if (targetCanvas === "main" && !isOffCanvas) {
                        displayAttrs = {
                          ...newAttrs,
                          _isInOffCanvasDuringDrag: false,
                        };
                      } else if (targetCanvas === "offCanvas" && isOffCanvas) {
                        displayAttrs = {
                          ...newAttrs,
                          _isInOffCanvasDuringDrag: true,
                        };
                      }
                    }
                  } catch (err) {
                    console.warn(
                      "Error converting line coords during drag move:",
                      err,
                    );
                  }
                }

                // CRITICAL FIX: Add throttling to match imageRenderer behavior when images are present
                // This prevents fluctuation during cross-canvas drag with images
                const now = Date.now();
                const shouldUpdate =
                  now - _lastLineRendererDragStateTime >=
                  DRAG_STATE_THROTTLE_MS;

                if (shouldUpdate) {
                  _lastLineRendererDragStateTime = now;

                  // Normal individual drag - call handleDragPreview same as circleRenderer
                  handleDragPreview?.({
                    event,
                    newAttrs,
                    sourceStageRef,
                    element: line,
                    type: elementType,
                    isOffCanvas,
                  });
                }

                // Pointer can preview the other stage before the node transfers. displayAttrs may use
                // that preview space; the panel must stay in the SOURCE stage space (where the node lives).
                const sourceStage = isOffCanvas ? "offCanvas" : "main";
                const previewOnOtherStage = previewTargetStage !== sourceStage;
                if (previewOnOtherStage) {
                  // Keep editor values in SOURCE stage coordinates while preview crosses stages.
                  const attrsBase = newAttrs;
                  const {
                    _isInOffCanvasDuringDrag: _unusedCross,
                    ...attrsCross
                  } = attrsBase;
                  const selectionAttrsCross = {
                    ...attrsCross,
                    type: elementType,
                    _isDragging: true,
                    _previewTargetStage: sourceStage,
                  };
                  setSelectedObject?.(selectionAttrsCross);
                  setSelectedObjects?.((prev) => {
                    const existingIndex = prev?.findIndex(
                      (obj) =>
                        obj.id === newAttrs.id &&
                        (obj.type === "Line" ||
                          obj.type === "Arrow" ||
                          obj.type === "DoubleSidedArrow"),
                    );
                    if (existingIndex >= 0 && prev) {
                      const updated = [...prev];
                      updated[existingIndex] = selectionAttrsCross;
                      return updated;
                    }
                    return prev ?? [];
                  });
                  return;
                }

                // CRITICAL: Do NOT pass _isInOffCanvasDuringDrag into setSelectedObject/setSelectedObjects
                // (same as circleRenderer) so findElementCanvas keeps element on current stage until drag end.
                // Pass _previewTargetStage so UnifiedLineArrowEditor shows x/y in the correct coordinate system (avoids wrong 0 or 1000+).
                const {
                  _isInOffCanvasDuringDrag: _unusedFlag,
                  ...attrsForSelection
                } = displayAttrs;
                const selectionAttrs = {
                  ...attrsForSelection,
                  type: elementType,
                  _isDragging: true,
                  // So editor shows x/y in correct coordinate system; clear when same stage
                  _previewTargetStage: previewTargetStage ?? undefined,
                };
                setSelectedObject?.(selectionAttrs);
                setSelectedObjects?.((prev) => {
                  const existingIndex = prev?.findIndex(
                    (obj) =>
                      obj.id === newAttrs.id &&
                      (obj.type === "Line" ||
                        obj.type === "Arrow" ||
                        obj.type === "DoubleSidedArrow"),
                  );
                  if (existingIndex >= 0 && prev) {
                    const updated = [...prev];
                    updated[existingIndex] = selectionAttrs;
                    return updated;
                  }
                  return prev ?? [];
                });
              }
            : undefined
        }
        onDragEnd={
          isInteractive
            ? (
                id,
                newX,
                newY,
                sourceStageRefParam,
                mouseClientX,
                mouseClientY,
              ) => {
                clearDragPreview?.();

                // CRITICAL: Get the current line properties from the Konva node
                // The node has the actual current rotation, scaleX, scaleY, etc. that the user sees
                let lineWithRotation = null;
                {
                  const stage =
                    sourceStageRefParam?.current ||
                    sourceStageRefParam ||
                    sourceStageRef?.current ||
                    sourceStageRef;
                  if (stage) {
                    const node = stage.findOne(`#${id}`);
                    if (node) {
                      const baseLine = (
                        isOffCanvas ? offCanvasLines : lines
                      ).find((line) => line.id === id);

                      if (baseLine) {
                        let nodeRotation = node.rotation();
                        nodeRotation = nodeRotation % 360;
                        if (nodeRotation < 0) nodeRotation += 360;

                        lineWithRotation = {
                          ...baseLine,
                          x: newX,
                          y: newY,
                          rotation: nodeRotation,
                          scaleX: node.scaleX(),
                          scaleY: node.scaleY(),
                        };
                      }
                    }
                  }
                }

                // Fallback: if we couldn't get the node, use the line from state
                if (!lineWithRotation) {
                  lineWithRotation = (
                    isOffCanvas ? offCanvasLines : lines
                  ).find((line) => line.id === id);
                  if (lineWithRotation) {
                    lineWithRotation = {
                      ...lineWithRotation,
                      x: newX,
                      y: newY,
                    };
                  }
                }

                // CRITICAL: Determine correct element type based on arrow flags
                let elementType = "Line";
                if (lineWithRotation) {
                  const hasStartArrow =
                    lineWithRotation.hasStartArrow === true ||
                    lineWithRotation.pointerAtBeginning === true;
                  const hasEndArrow =
                    lineWithRotation.hasEndArrow === true ||
                    lineWithRotation.pointerAtEnd === true;
                  if (hasStartArrow && hasEndArrow) {
                    elementType = "DoubleSidedArrow";
                  } else if (hasStartArrow || hasEndArrow) {
                    elementType = "Arrow";
                  }
                }

                // Route drag-end to the right handler family:
                // - Unified line/arrow data (stored in lines arrays) must use the Line path.
                // - Legacy arrow arrays must keep Arrow/DoubleSidedArrow type.
                const isUnifiedLineElement =
                  lines.some((l) => l.id === id) ||
                  offCanvasLines.some((l) => l.id === id);
                const typeForDragEnd = isUnifiedLineElement
                  ? "Line"
                  : elementType;

                // Always use handleElementDragEnd for proper transfer/clamping across canvases.
                handleElementDragEnd({
                  id,
                  newX,
                  newY,
                  type: typeForDragEnd,
                  sourceStageRef: sourceStageRefParam || sourceStageRef,
                  mainState: lines,
                  offCanvasState: offCanvasLines,
                  setMainState: setLines,
                  setOffCanvasState: setOffCanvasLines,
                  setSelectedObjects,
                  currentLine: lineWithRotation,
                  currentArrow:
                    !isUnifiedLineElement && elementType === "Arrow"
                      ? lineWithRotation
                      : undefined,
                  currentDoubleSidedArrow:
                    !isUnifiedLineElement &&
                    elementType === "DoubleSidedArrow"
                      ? lineWithRotation
                      : undefined,
                  mouseClientX,
                  mouseClientY,
                  handleLineDragEnd: handleLineDragEnd,
                });
              }
            : undefined
        }
        onUpdate={
          isInteractive
            ? (
                id,
                absPoints,
                bends,
                angle,
                groupX,
                groupY,
                width,
                height,
                e,
              ) => {
                // Convert absolute points to relative coordinates
                const relativePoints = absPoints.map((coord, i) =>
                  i % 2 === 0 ? coord - groupX : coord - groupY,
                );

                const updatedElement = {
                  ...element,
                  id,
                  points: relativePoints, // Store as relative coordinates
                  x: groupX,
                  y: groupY,
                  width,
                  height,
                  angle,
                };

                // Use updateLineProperties for consistent state updates and history tracking
                // This ensures the state is properly updated and changes are tracked
                if (updateLineProperties) {
                  updateLineProperties(id, {
                    points: relativePoints,
                    x: groupX,
                    y: groupY,
                    width,
                    height,
                    angle,
                    skipOffset: true, // Preserve the exact position/geometry from drag
                  });
                } else {
                  // Fallback to direct state update if updateLineProperties is not available
                  setLineState((prev) => {
                    const updated = prev.map((l) =>
                      l.id === id ? updatedElement : l,
                    );
                    return updated;
                  });
                  setSelectedObject({
                    ...updatedElement,
                    type: updatedElement.type || "Line",
                  });
                  setSelectedLine?.(updatedElement);
                }
              }
            : undefined
        }
        updateElementProperties={updateLineProperties}
        isOffCanvas={isOffCanvas}
        isPreview={isPreview}
        hideBlueCircles={effectiveHideBlueCircles}
        forceShowBlueCircles={isPreview}
        forceHideBlueCircles={isSourceHiddenForPreview}
        isMultiSelectMode={isMultiSelectMode}
        selectedObjectsArray={selectedObjects || []}
        onDotDragMove={
          isInteractive
            ? (updatedElement) => {
                setSelectedObject({
                  ...updatedElement,
                  type: updatedElement.type || "Line",
                });
                setLineState((prev) =>
                  prev.map((l) =>
                    l.id === updatedElement.id ? updatedElement : l,
                  ),
                );
                setSelectedLine?.(updatedElement);
              }
            : undefined
        }
        onCopyToOppositeCanvas={
          isInteractive && handleElementCopy && isOffCanvas
            ? () => handleElementCopy(line.id, "Line", "offCanvas")
            : undefined
        }
        canvasWidth={canvasWidth}
        canvasHeight={canvasHeight}
      />
    );
  };
};
